export type Language = "zh-CN" | "en";

const en = {
  codexEventsTotal: "{count} available events",
  codexEventsLatest: "Latest page",
  codexEventsNew: "{count} new events; the current page is preserved.",
  codexEventsLegacy: "Legacy snapshot: only retained events are available.",
  codexEventsMissing:
    "{count} earlier events were not retained and cannot be restored.",
  codexEventsTruncated: "This event text was truncated when recorded.",

  codexConfigSource: "Codex configuration",
  codexConfigCustom: "Custom connection",
  codexConfigLocal: "Server account configuration",
  codexConfigLocalHelp:
    "Use the server account's Codex settings and sign-in. Blank model and effort inherit its defaults.",
  codexConfigInherit: "Use server configuration",
  codexLocalScopeHelp:
    "Explicitly permits this key to use the server account's Codex configuration and credentials.",
  knowledgeUpdate: "Update knowledge base",
  knowledgeUpdateSelected: "Update knowledge base ({count})",
  knowledgeUpdateScope: "Selected repositories",
  knowledgeUpdateScopeHelp:
    "Only the repositories listed below will be updated.",
  knowledgeUpdateAdminOnly:
    "An administrator account is required to update the knowledge base.",
  knowledgeUpdateEmpty: "Select at least one repository.",
  knowledgeUpdateNotReady:
    "Selected sources are not ready or are excluded from source copying. Prepare them in source management first.",
  knowledgeUpdateStart: "Start update",
  knowledgeUpdateStop: "Stop",
  knowledgeUpdateStopping: "Stopping…",
  knowledgeUpdateStopped: "Stopped",
  knowledgeUpdateStopFailed: "Could not stop this update: {message}",
  knowledgeUpdateQueued: "Queued",
  knowledgeUpdateAlreadyRunning:
    "Some selected repositories already have an update in progress.",
  knowledgeUpdateViewExisting: "View running update",
  knowledgeUpdateValidation: "Import summary",
  knowledgeUpdateCoverageComplete: "Knowledge coverage is complete.",
  knowledgeUpdateCoveragePartial:
    "This report does not establish full knowledge coverage. Review the import summary.",
  knowledgeUpdateValidationPending:
    "No import summary is available yet. Review the task output for details.",
  knowledgeUpdateVersions: "Fixed source versions",
  knowledgeUpdateAgain: "Configure another update",
  knowledgeUpdateHistory: "Open Codex run history",
  knowledgeUpdateCatalogError:
    "Model catalog unavailable. Enter a custom model or retry.",
  overviewInputTokens: "Input tokens (including cache)",
  overviewInputCache: "Input cache / cache rate",
  overviewOutputTokens: "Output tokens (including reasoning)",
  overviewCacheNote:
    "Cache rate = cached tokens / total tokens for that direction. The SDK does not report output cache.",
  docsKeySingleSource:
    "Configure the API key only in its dedicated field; remove X-API-Key from custom headers.",
  docsChooseFile: "Choose a file for this multipart request.",
  docsPrefillHelp:
    "Method, path, headers and sample body follow the selected endpoint. " +
    "Replace path/query variables and sample values before sending. Nothing is sent automatically. " +
    "Configure authentication only in the API key field; it is added to the request when sending.",
  docsKeyPlaceholder: "Enter your API key; retained when switching endpoints",
  docsBodyPlaceholder: "JSON parameters; GET and HEAD have no body",
  docsReplacePlaceholders:
    "Replace path/query placeholders and configure the API key before sending.",
  docsLocalPath: "Use a same-origin /api/ path.",
  docsUploadFile: "Upload file (multipart fields are shown above)",
  usageDate: "Date",
  usageCacheNote:
    "Cached input is included in input; total usage does not add it again.",
  usageInput: "Input tokens",
  usageOutput: "Output tokens",
  usageCached: "Cached input tokens",
  usageCacheWrite: "Cache write tokens (detail only)",
  usageTotal: "Total tokens",
  usageReasoning: "Reasoning output tokens",
  usageUnavailable: "Not provided",
  usageNote:
    "Cumulative usage for this execution. Cached input is included in input and reasoning in output. " +
    "Cache details are not added to the total.",
  usageWeek: "Codex usage · last 7 days",
  usageCoverage: "{samples} of {runs} runs reported; {missing} missing",
  usageDuration: "Duration",
  usageAverage: "Cumulative average processing duration",
  usageHistoryNote:
    "Historical statistics were backfilled from existing records; records removed before backfill are excluded.",
  usageTimezone:
    "Daily totals use {timezone}. Availability checks are excluded.",
  usageExecutions: "Executions",

  overview: "Overview",
  knowledge: "Knowledge",
  rag: "RAG retrieval",
  jobs: "Scheduled jobs",
  history: "Execution history",
  logs: "System logs",
  keys: "API keys",
  users: "Users & roles",
  codex: "Codex debugger",
  skills: "Skills",
  sources: "Source repositories",
  sourceDescription:
    "Select repositories here; manage automatic updates in the built-in scheduled task.",
  sourceAll: "Update all",
  sourceSelected: "Update selected ({count})",
  sourceOne: "Update",
  sourceLatest: "Remote freshness",
  sourceVerified: "Up to date",
  sourceStale: "Out of date",
  sourceUnknown: "Not verified",
  sourceLast: "Last update",
  sourceNext: "Next scheduled update",
  sourceScheduleLink: "Manage update schedule",
  sourceResult: "Last update result",
  sourceHistory: "Update history",
  sourceSuccess: "Success",
  sourceFailed: "Failed",
  sourcePending: "Queued",
  sourceRunning: "Updating",
  sourceRetrying: "Retrying",
  sourceStart: "Started",
  sourceEnd: "Finished",
  sourceDuration: "Duration",
  sourceCommit: "Updated commit ID",
  sourceError: "Failure / retry details",
  sourceBranch: "Branch",
  sourceBatch: "Batch details",
  sourceOmissions: "Paths not representable on Windows (Git blobs retained)",
  sourceManual: "Manual",
  sourceHourly: "Scheduled / initialization",
  sourceAutoOff: "Automatic source updates disabled",
  sourceAccepted: "Update requested. This page refreshes every 3 seconds.",
  sourceEmpty: "No update history yet.",
  sourceTrigger: "Trigger",
  sourceTotal: "Repositories",
  sourceInterrupted: "Interrupted",
  sourceExecuting: "In progress",
  sourceStop: "Stop",
  sourceStopping: "Stopping",
  sourceStopped: "Stopped",
  sourceBehind: "Behind by {count} commits",
  sourceDistanceUnknown: "Commit count unconfirmed",
  sourceProgress: "Update progress",
  sourceProgressWaiting: "Waiting for Git progress output…",
  sourceSkipped: "Skipped (already busy)",
  sourceRemote: "Remote branch HEAD",
  sourceRemoteChecked: "Remote HEAD checked",
  sourceAuto: "Keep up to date",
  sourceCopy: "Copy to tasks",
  sourceAdd: "Add repository",
  sourceEdit: "Edit repository",
  sourceUrl: "Git HTTPS URL",
  sourceSaved: "Repository configuration saved.",
  sourceDelete:
    "Delete repository and its shared cache? Active task copies are retained.",
  docs: "API Explorer",
  signOut: "Sign out",
  signIn: "Sign in",
  profile: "Profile",
  changePassword: "Change password",
  currentPassword: "Current password",
  newPassword: "New password",
  registerAction: "Register",
  setupTitle: "Initialize administrator",
  setupCode: "Local verification code",
  setupHelp:
    "Read this file in the application directory on the server and enter its code. " +
    "The first administrator is activated immediately.",
  setupSuccess: "Administrator created. Sign in with your new account.",
  registerTitle: "Create account",
  registerEmail: "Email",
  registerConfirmPassword: "Confirm password",
  registerPending:
    "Registration submitted. An administrator must approve your account.",
  operational: "All systems operational",
  menu: "Menu",
  start: "Start",
  language: "中文",
  loading: "Loading...",
  create: "Create",
  remove: "Delete",
  edit: "Edit",
  run: "Run",
  name: "Name",
  state: "State",
  action: "Action",
  actions: "Actions",
  empty: "No records.",
  details: "Details",
  preview: "Preview",
  close: "Close",
  raw: "Raw data",
  save: "Save",
  cancel: "Cancel",
  working: "Working...",
  error: "Error",
  success: "Success",
  enabled: "Enabled",
  disabled: "Disabled",
  selectAll: "Select all",
  deselectAll: "Deselect all",
  active: "Active",
  enable: "Enable",
  disable: "Disable",
  previous: "Previous",
  next: "Next",
  goToPage: "Go",
  paginationLabel: "Pagination",
  paginationPageSummary: "Page {page} of {total}",
  paginationPageInput: "Page",
  paginationInvalidPage: "Enter a whole number from 1 to {total}.",
  refresh: "Refresh",
  sortAscending: "Sort {column} ascending",
  sortDescending: "Sort {column} descending",
  sortUnsorted: "Clear sorting by {column}",
  codexMissingFailureReason:
    "The server did not record a failure reason. See the run details and request ID for investigation.",
  codexCustomModel: "Custom model",
  codexExecutionMode: "Execution mode",
  codexTimeoutSeconds: "Time limit (seconds, 1–{seconds}; up to {hours} hours)",
  codexRestricted: "Restricted",
  codexUnrestricted: "Unrestricted (administrator or privileged API key)",
  tableColumnResizeHelp:
    "Drag to resize; Left/Right adjusts 10 px, Shift adjusts 50 px",
  tableColumnMoveHelp:
    "Drag to move column, or focus and press Alt + Left/Right",
  selectVisible: "Select visible",
  deselectVisible: "Deselect visible",
  shellSubtitle: "compatibility operations",
  shellControlPlane: "CONTROL PLANE",
  breadcrumbConsole: "Console",
  backToOverview: "Back to overview",
  loginHeading: "CANGJIE CAMPAT / SECURE LOGIN",
  loginTitle: "Compat Campat",
  loginDescription: "Knowledge operations console",
  loginMarquee: "AUTHORIZED ACCESS / LOCAL CONTROL PLANE",
  loginUsername: "Email or username",
  wallpaperTitle: "Display Properties",
  wallpaperDesktop: "Desktop",
  wallpaperStars: "Stars",
  wallpaperPipes: "Pipes",
  wallpaperMaze: "3D Maze",
  wallpaperPause: "Pause animation",
  wallpaperPlay: "Play animation",
  loginPassword: "Password",
  loginSubmit: "Open control plane",
  loginDefaultStatus: "Use your administrator credentials.",
  loginDocs: "View public API Explorer",
  overviewKicker: "OVERVIEW / OPERATIONS",
  overviewTitle: "Control plane overview",
  overviewDescription:
    "Service posture, resource inventory, and recent automation state.",
  overviewMarquee:
    "PUBLIC READ API ONLINE / WRITE OPERATIONS REQUIRE X-API-KEY",
  overviewKnowledgeTables: "Knowledge resources",
  overviewRepositories: "Repositories",
  overviewComponents: "Components",
  overviewFiles: "Files",
  overviewSymbols: "Symbols",
  overviewContracts: "Contracts",
  overviewEvidence: "Evidence",
  overviewBehaviors: "Behaviors",
  overviewConsumers: "Consumers",
  overviewScheduledJobs: "Scheduled jobs",
  overviewActiveJobs: "Enabled jobs",
  overviewSkills: "Skills",
  overviewApiKeys: "API keys",
  overviewRagBackend: "RAG backend",
  overviewOnline: "ONLINE",
  overviewChecking: "Checking...",
  overviewInventory: "Operational inventory",
  overviewRecentJobs: "Recent scheduled jobs",
  overviewRecentRuns: "Recent Codex runs",
  overviewEmptyRuns: "No Codex runs have been recorded.",
  overviewRunModel: "Model",
  overviewRunStatus: "Status",
  overviewRunTime: "Started",
  overviewLoading: "Loading operational inventory...",
  overviewEmptyJobs: "No scheduled jobs have been configured.",
  overviewJob: "Job",
  overviewCommand: "Command",
  overviewState: "State",
  overviewLoadError: "Unable to load operational inventory: {message}",
  formInvalidJson: "Enter valid JSON.",
  formObjectRequired: "Enter a JSON object.",
  formFieldsRequired: "Complete all required fields.",

  knowledgeField_has_markdown: "Markdown content",
  knowledgeField_markdown_document_id: "Linked Markdown document",
  knowledgeChooseRepositoryFirst: "Choose a repository first",
  knowledgeYes: "Yes",
  knowledgeNo: "No",
  knowledgeChooseReference: "Search and choose a record",
  knowledgeNewLocation: "Create or reuse a source location on save",
  knowledgeExistingLocation: "Choose an existing location",
  knowledgeHasMarkdown: "Converted Markdown content",
  knowledgeTitle: "Knowledge resources",
  knowledgeDescription: "Search, inspect, and manage compatibility records.",
  knowledgeArchitectureTitle: "Knowledge base architecture",
  knowledgeArchitectureDescription:
    "Follow source locations to inspect linked symbols, contracts, evidence, and review decisions.",
  knowledgeArchitectureMapLabel: "Knowledge base architecture map",
  knowledgeArchitectureSources: "SOURCE REGISTRY",
  knowledgeArchitectureRepositories: "Repositories",
  knowledgeArchitectureRepositoriesHint: "Project and platform identity",
  knowledgeArchitectureFiles: "Files",
  knowledgeArchitectureFilesHint: "Markdown, source, and imported documents",
  knowledgeArchitectureIndex: "STRUCTURAL INDEX",
  knowledgeArchitectureLocations: "Locations",
  knowledgeArchitectureLocationsHint: "Line, page, and section coordinates",
  knowledgeArchitectureChunks: "Chunks",
  knowledgeArchitectureChunksHint: "Searchable document and source passages",
  knowledgeArchitectureSymbols: "Symbols",
  knowledgeArchitectureSymbolsHint: "Named declarations and signatures",
  knowledgeArchitectureSemantic: "SEMANTIC VIEWS",
  knowledgeArchitectureContracts: "Contracts",
  knowledgeArchitectureContractsHint: "Compatibility commitments and risk",
  knowledgeArchitectureEvidence: "Evidence",
  knowledgeArchitectureEvidenceHint: "Observed facts with provenance",
  knowledgeArchitectureCases: "Cases",
  knowledgeArchitectureCasesHint: "Review decisions and linked records",
  knowledgeArchitectureRelationNote:
    "Arrows show record relationships, not an automatic processing pipeline. Select a record to open its query view.",
  knowledgeArchitectureLocationLink: "source coordinates",
  knowledgeArchitectureCaseLink: "review references",
  knowledgeArchitectureSourceLink: "namespace",
  knowledgeArchitectureViewsTitle: "Open a view",
  knowledgeArchitectureViewsDescription:
    "Each view is a maintained projection over the same knowledge graph. Choose one to inspect records.",
  knowledgeCreate: "Create record",
  knowledgeUploadFile: "Upload file",
  upload: "Upload",
  file: "File",
  namespace: "Namespace",
  relative_path: "Relative path",
  knowledgeSearchPlaceholder: "Type to search...",
  knowledgeTable: "Table",
  knowledgeSearch: "Search",
  knowledgePageSize: "Page size",
  knowledgeRecords: "{table} records",
  knowledgePageSummary: "Page {page} / {count} results",
  knowledgeRecordDetail: "Record detail",
  knowledgeEmpty: "No knowledge records match the current filters.",
  knowledgeInvalidPage: "Enter a page within the available range.",
  knowledgeCreateTitle: "Create knowledge record",
  knowledgeEditTitle: "Edit knowledge record",
  knowledgeDeleteTitle: "Delete knowledge record",
  knowledgeConfirmDelete: "Delete record {id}?",
  knowledgeCreated: "Knowledge record created.",
  knowledgeUpdated: "Knowledge record updated.",
  knowledgeDeleted: "Knowledge record deleted.",
  knowledgeTabSearch: "Search",
  knowledgeTabContracts: "Contracts",
  knowledgeTabEvidence: "Evidence",
  knowledgeTabSymbols: "Symbols",
  knowledgeTabFiles: "Files",
  knowledgeTabRepositories: "Repositories",
  knowledgeTabCases: "Cases",
  knowledgeTab_search: "Knowledge architecture",
  knowledgeTab_contracts: "Contracts",
  knowledgeTab_evidence: "Evidence",
  knowledgeTab_symbols: "Symbols",
  knowledgeTab_chunks: "Chunks",
  knowledgeTab_files: "Files",
  knowledgeTab_documents: "Markdown documents",
  knowledgeDownloadContent: "Download full document",
  knowledgePreviewLimited:
    "Preview shows the first 200,000 characters. Download to read the full document.",
  knowledgeUploadLimits:
    "Upload a .md, .txt, .pdf or .docx file no larger than 20 MiB.",
  knowledgeTab_locations: "Locations",
  knowledgeTab_components: "Components",
  knowledgeTab_repositories: "Repositories",
  knowledgeTab_cases: "Cases",
  "knowledgeTab_ingestion-sources": "Ingestion sources",
  knowledgeSelect: "Select",
  knowledgeOnePerLine: "One value per line.",
  knowledgeContractDetail: "Typed contract detail",
  knowledgeEvidenceLinks: "Evidence links",
  knowledgeAddEvidence: "Add evidence",
  knowledgeAddBehavior: "Add behavior",
  knowledgeAddConsumer: "Add consumer",
  knowledgeObservations: "Observations",
  knowledgeAddObservation: "Add observation",
  knowledgeRelationCreated: "Related record created.",
  knowledgeRelationDeleted: "Related record deleted.",
  knowledgeFileMetadata: "File metadata",
  knowledgeFileContent: "File content",
  knowledgeBehaviors: "Behaviors",
  knowledgeConsumers: "Consumers",
  knowledgeHardDeleteHint:
    "This permanently deletes the resource. Referenced resources cannot be deleted.",
  knowledgeDeleteFailed: "Delete failed. The resource may still be in use",
  knowledgeDeleteAction: "Delete",
  knowledgeSuggestionsLoading: "Loading suggestions...",
  knowledgeSuggestionsError: "Suggestions are temporarily unavailable.",
  knowledgeResultCount: "{count} results",
  knowledgeQueryRequired: "Enter a query to search the knowledge base.",
  knowledgeTypes: "Resource types",
  knowledgeType: "Type",
  knowledgeMatch: "Match mode",
  knowledgeMinimumMatch: "Minimum terms",
  knowledgeRepository: "Repository",
  knowledgeComponent: "Component",
  knowledgeKind: "Kind",
  knowledgeFamily: "Family",
  knowledgeRisk: "Risk",
  knowledgeLevel: "Evidence level",
  knowledgeIngestionSource: "Ingestion source",
  knowledgePath: "Source path",
  knowledgeCitation: "Citation",
  knowledgeTitleColumn: "Title",
  knowledgeSummary: "Summary",
  knowledgeAll: "All",

  keysKicker: "ACCESS / CREDENTIALS",
  keysTitle: "API keys",
  keysDescription: "Scoped credentials; plaintext is shown once at creation.",
  keysGenerate: "Generate key",
  keysCopySecret: "Copy secret now",
  keysInventory: "Credential inventory",
  keysName: "Credential name",
  keysPrefix: "Prefix",
  keysScopes: "Scopes",
  keysCreated: "Created",
  keysLastUsed: "Last used",
  keysCreateTitle: "Generate API key",
  keysDeleteRecord: "Delete record",
  keysDeletedStatus: "Revoked key record deleted.",
  keysConfirmDelete:
    "Permanently delete this revoked key record? Audit history is retained.",
  keysRevoke: "Revoke",
  keysConfirmRevoke: "Revoke this API key?",
  keysEmpty: "No API keys have been created.",
  keysCreatedStatus: "API key created. Copy the secret now.",
  keysRevokedStatus: "API key revoked.",

  usersKicker: "ACCESS / IDENTITY",
  settings: "System settings",
  settingsDescription: "Manage execution and queue capacity.",
  settingsAdminOnly: "Only administrators can access system settings.",
  capacityTitle: "Request capacity",
  capacityExecution: "Maximum active long requests (10–100)",
  capacityQueue: "Maximum queued requests (0–100)",
  capacityActive: "Currently executing",
  capacityWaiting: "Currently queued",
  capacityHelp:
    "Applies to Codex execution and model-query HTTP requests. The asynchronous knowledge-update queue is separate.",
  capacityBehavior:
    "Requests run while execution slots are available, then queue; a full queue rejects new requests with HTTP 503. " +
    "Saving applies immediately. Lower limits do not terminate existing executions or queued requests. " +
    "Refresh reloads saved limits and current counts.",
  capacityInvalid:
    "Execution must be an integer from 10 to 100; queue capacity must be an integer from 0 to 100.",
  capacitySaved: "Saved and applied immediately.",
  usersTitle: "Users & roles",
  usersDescription: "Manage console identities, roles, and activation.",
  usersAdd: "Add user",
  usersDirectory: "Identity directory",
  usersUser: "User",
  usersRole: "Role",
  usersLastLogin: "Last login",
  usersPassword: "Password",
  usersNewPassword: "New password",
  usersCreateTitle: "Add console user",
  usersEditTitle: "Edit console user",
  usersDelete: "Delete user",
  usersConfirmDelete: "Delete {username}?",
  usersEmpty: "No console users found.",
  usersCreatedStatus: "Console user created.",
  usersUpdatedStatus: "Console user updated.",
  usersApprovedStatus: "Console user approved.",
  usersApprove: "Approve",
  usersDeletedStatus: "Console user deleted.",
  usersPasswordOptional: "Leave blank to keep the current password.",

  jobsKicker: "AUTOMATION / SCHEDULER",
  jobsTitle: "Scheduled jobs",
  jobsDescription:
    "Python commands, schedules, environment keys, and execution state.",
  jobsCreate: "Create job",
  jobsInventory: "Job inventory",
  jobsCommand: "Command",
  jobsShell: "Shell",
  jobsCategory: "Task category",
  jobsCategoryScript: "Script",
  jobsCategoryCodex: "Codex",
  jobsProviderBaseUrl: "Provider base URL",
  jobsProviderApiKey: "Provider API key",
  jobsProviderApiKeyKeep: "Leave blank to keep current key",
  jobsModel: "Model",
  jobsEffort: "Reasoning effort",
  jobsSkills: "Skills",
  jobsSkillsPlaceholder: "Comma-separated skill names",
  jobsSystemPrompt: "System prompt",
  jobsPrompt: "Prompt",
  jobsScript: "Inline script",
  jobsWorkingDirectory: "Working directory",
  jobsInterval: "Interval (seconds)",
  jobsTimeout: "Timeout (seconds, 1–{seconds}; up to {hours} hours)",
  jobsEnvironment: "Environment variables",
  jobsEnvironmentKey: "Key",
  jobsEnvironmentValue: "Value",
  jobsEnvironmentKeep: "Stored value — leave blank to keep",
  jobsEnvironmentClear: "Clear all stored variables",
  jobsEnvironmentKeyInvalid: "Use a valid environment variable name.",
  jobsEnvironmentDuplicate: "Environment variable names must be unique.",
  jobsShowEnvironmentValue: "Show value",
  jobsHideEnvironmentValue: "Hide value",
  jobsAddEnvironment: "Add variable",
  jobsDescriptionMarkdown: "Description (Markdown)",
  jobsTaskType: "Task type",
  jobsTaskTypeBash: "Bash script",
  jobsTaskTypePowershell: "PowerShell script",
  jobsTaskTypeCmd: "CMD script",
  jobsBuiltinSource: "Built-in source repository refresh",
  jobsBuiltinSourceHelp:
    "The fixed system script reads the repository catalog maintained in Source repositories. " +
    "Change repository selection there.",
  jobsStartAt: "Schedule start (local time)",
  jobsStartAtHelp:
    "Optional start boundary. Leave empty to retain the saved schedule anchor; Cron runs at the next matching time.",
  jobsTaskTypePython: "Python script",
  jobsTaskTypeJavascript: "JavaScript script",
  jobsTaskTypeCodex: "Codex task",
  jobsScheduleMode: "Schedule mode",
  jobsScheduleModeFixed: "Fixed time daily",
  jobsScheduleModeInterval: "Fixed interval",
  jobsScheduleModeCron: "Cron expression",
  jobsCronExpression: "Cron expression",
  jobsCronHelp:
    "Five fields: minute hour day month weekday. Use mon-fri for weekdays (0 = Monday).",
  jobsTimezone: "Time zone (IANA)",
  historyAllTasks: "All tasks",
  historyExportExcel: "Export Excel",
  historyExportFrom: "Start date",
  historyExportTo: "End date (inclusive)",
  historyExportHelp:
    "Export all stored records matching the current filters and date range, across every page.",
  historyExportInvalidDate:
    "Choose a valid date range with the end date on or after the start date.",
  historyExportBusy: "Exporting…",
  jobsFixedHour: "Hour (0-23)",
  jobsFixedMinute: "Minute (0-59)",
  jobsStartHour: "Start hour (0-23)",
  jobsStartMinute: "Start minute (0-59)",
  jobsEndHour: "End hour (0-23)",
  jobsEndMinute: "End minute (0-59)",
  jobsIntervalSeconds: "Interval (seconds)",
  jobsCreateTitle: "Create scheduled job",
  jobsEditTitle: "Edit scheduled job",
  jobsConfirmDelete: "Delete task configuration?",
  jobsEmpty: "No scheduled jobs configured.",
  jobsCreatedStatus: "Scheduled job created.",
  jobsUpdatedStatus: "Scheduled job updated.",
  jobsDeletedStatus: "Scheduled job deleted.",
  jobsQueuedStatus: "Job execution queued.",
  jobsEnabled: "Enabled",

  historyKicker: "AUTOMATION / AUDIT",
  historyTitle: "Execution history",
  historyDescription: "Select a task and inspect its runs, output, and status.",
  historyTask: "Task",
  historyNoTasks: "No tasks available",
  historyRuns: "Runs",
  historyRun: "Run",
  historyStatus: "Status",
  historyStarted: "Started",
  historyFinished: "Finished",
  historyReturnCode: "Return code",
  historyStdout: "Standard output",
  historyStderr: "Standard error",
  historyRunDetail: "Run detail",
  historyEmpty: "This task has no execution history.",

  logsKicker: "OBSERVABILITY / SYSTEM",
  logsTitle: "API audit logs",
  logsDescription: "Structured API request events, independent of system logs.",
  logsLatest: "Latest events",
  logsTimestamp: "Timestamp",
  logsLevel: "Level",
  logsEvent: "Event",
  logsMessage: "Message",
  logsCorrelation: "Correlation",
  logsFilters: "Filters",
  logsAll: "All levels",
  logsEmpty: "No log events match the current filters.",
  logsTask: "Task",
  logsFrom: "From",
  logsTo: "To",
  logsMethod: "Method",
  logsStatus: "Status",
  logsActor: "Actor",
  logsResource: "Resource",
  logsDuration: "Duration",
  logsDetail: "Audit detail",
  logsDetailLoading: "Loading audit detail...",
  logsDetailLoadError: "Unable to load audit detail: {message}",
  logsRequestId: "Request ID",
  logsPath: "Path",
  logsFields: "Request fields",
  logsRedaction: "Redaction",

  ragKicker: "RETRIEVAL / LANCEDB",
  ragTitle: "RAG retrieval",
  ragDescription:
    "Inspect ranked compatibility context and its retrieval backend.",
  ragRequest: "Retrieval request",
  ragQuestion: "Compatibility question",
  ragQuestionPlaceholder: "Ask a compatibility question",
  ragTopK: "Top K",
  ragSearch: "Search corpus",
  ragResults: "Ranked results",
  ragInitial: "Enter a compatibility question.",
  ragLanceStatus: "LanceDB vector search",
  ragError: "Retrieval failed: {message}",
  ragEmpty: "No relevant context was returned.",
  ragResultFallback: "Result {index}",

  codexKicker: "AI / PROVIDER GATEWAY",
  codexTitle: "Codex debugger",
  codexDescription:
    "Execute an OpenAI-compatible request and inspect normalized output.",
  codexConfiguration: "Request configuration",
  codexProviderKey: "Provider API key",
  codexProtocol: "Upstream protocol",
  codexRequestedEffort: "Requested reasoning effort",
  codexEffectiveEffort: "Effective reasoning effort",
  codexEffortOmitted: "Not sent (model default)",
  codexEffortAdjustments: "Reasoning adjustments",
  codexBaseUrl: "Base URL",
  codexModel: "Model",
  codexEffort: "Reasoning effort",
  codexSkills: "Skills",
  codexSkillsHelp: "Select enabled skills to activate for this run.",
  skillMultiSelectTitle: "Skills",
  skillMultiSelectFilter: "Filter skills",
  skillMultiSelectEmpty: "No matching skills.",
  codexSystemPrompt: "System prompt",
  codexPrompt: "Prompt",
  codexEnvironment: "Environment variables",
  codexEnvironmentKey: "Key",
  codexEnvironmentValue: "Value",
  codexEnvironmentKeyInvalid: "Use a valid environment variable name.",
  codexEnvironmentDuplicate: "Environment variable names must be unique.",
  codexShowEnvironmentValue: "Show value",
  codexHideEnvironmentValue: "Hide value",
  codexAddEnvironment: "Add variable",
  codexExecute: "Execute request",
  codexResult: "Execution result",
  codexResultModel: "Model",
  codexResultEffort: "Reasoning effort",
  codexResultUsage: "Token usage",
  codexResultLatency: "Latency",
  codexResultRequestId: "Request ID",
  codexInitial: "No execution yet.",
  codexInvalidEnvironment: "Environment variables must be a JSON object.",
  codexError: "Execution failed: {message}",
  codexHistoryTitle: "Run history",
  codexHistoryStatus: "Status",
  codexHistoryAll: "All",
  codexHistoryAvailabilityProbe: "Model availability probe",
  codexHistoryProbeYes: "Yes",
  codexHistoryProbeNo: "No",
  codexHistoryRunning: "Running",
  codexProgressTitle: "Execution progress",
  codexProgressRefresh:
    "Refreshes every 3 seconds; steps remain available after completion.",
  codexProgressOmitted:
    "{count} earlier events omitted. Latest 200 events retained; 4000 characters per event.",
  codexProgressEmpty:
    "No recorded steps yet (older runs have no execution timeline).",
  codexHistorySucceeded: "Succeeded",
  codexHistoryFailed: "Failed",
  codexHistoryFrom: "Start time from",
  codexHistoryTo: "Start time to",
  codexHistoryTimeHelp:
    "Inclusive local task creation/acceptance time. Queued tasks may begin model execution later.",
  codexHistoryPageSize: "Rows per page",
  codexHistoryFilter: "Apply filters",
  codexHistoryCreated: "Started",
  codexHistoryEmpty: "No Codex runs match these filters.",
  codexHistoryLoadError: "Unable to load run history: {message}",
  codexHistoryDetail: "Codex run detail",
  codexHistoryDetailError: "Unable to load run detail: {message}",
  codexHistoryOutput: "AI output",
  codexHistoryRequest: "Request",
  codexHistorySystemPrompt: "System prompt",
  codexHistorySkills: "Skills",
  codexHistoryEnvKeys: "Environment key names",
  codexHistoryWorkspace: "Debug workspace",
  codexHistoryKeyHashLength: "API key hash length",
  codexHistoryError: "Error",
  skillsKicker: "AI / SKILL REPOSITORY",
  skillsTitle: "Skills repository",
  skillsDescription:
    "Manage validated skills available to isolated Codex runs.",
  skillsInventory: "Installed skills",
  skillImport: "Import or replace skill",
  skillDescription: "Description",
  skillValidation: "Validation",
  skillUpdated: "Updated",
  skillEmpty: "No skills have been installed.",
  skillEnabled: "Skill enabled.",
  skillDisabled: "Skill disabled.",
  skillImported: "Skill imported.",
  skillDeleted: "Skill deleted.",
  skillConfirmDelete: "Delete skill {name}?",
  skillPreviewTitle: "Skill details: {name}",
  skillPreviewLoading: "Loading skill details...",
  skillFileCount: "Files",
  skillSize: "Size (bytes)",
  skillFiles: "Skill files",
  skillFilesEmpty: "This skill has no files to display.",
  skillFileSelect: "Select a text file to preview or edit.",
  skillFileText: "Text",
  skillFileReadOnly: "Read-only binary or oversized file",
  skillFileLoading: "Loading file...",
  skillFileContent: "Contents of {path}",
  skillFileUnsaved: "Unsaved changes",
  skillFileSaved: "Saved {path}.",

  docsKicker: "PUBLIC DEVELOPER RESOURCE",
  docsTitle: "Cangjie Compat API Explorer",
  docsDescription:
    "Live contracts for the external knowledge, RAG, and Codex APIs.",
  docsConsole: "Administrator console",
  docsDebugger: "Online request debugger",
  docsMethod: "Method",
  docsPath: "Path",
  docsHeaders: "Additional headers (JSON)",
  docsApiKey: "API key",
  docsBody: "JSON body",
  docsSend: "Send request",
  docsTryIt: "Try this endpoint",
  docsLoading: "Loading API contracts...",
  docsResponse: "Response",
  docsResponsePending: "Response will appear here.",
  docsLoadError: "Unable to load API contracts: {message}",
  docsInvalidHeaders: "Additional headers must be a JSON object.",
  docsInvalidBody: "Request body must be valid JSON.",
  docsNetworkError: "Network request failed: {message}",
  docsHttpResult: "HTTP {status} {statusText}",
  docsAuthentication: "Authentication",
  docsAuthDescription:
    "Protected operations accept an API key in the X-API-Key header.",
  docsAuthRequired: "X-API-Key required ({scope})",
  docsAuthNotRequired: "No X-API-Key required",
  docsEndpointPurpose: "Purpose",
  docsEndpoints: "Endpoints",
  docsPublic: "Public",
  docsSession: "Session",
  docsApiKeyAuth: "API key",
  docsQuery: "Query parameters",
  docsNoQuery: "This endpoint has no query parameters.",
  docsRequestSchema: "Request body",
  docsResponseExample: "Response example",
  docsResponseSchema: "Response contract",
  docsErrors: "Errors",
  docsTables: "Allowed tables",
  docsExamples: "Examples",
  docsCurl: "curl",
  docsPython: "Python",
  docsJavaScript: "JavaScript",
  docsNoRequestBody: "This endpoint has no request body.",
  docsNoErrors: "Standard HTTP and authorization errors apply.",
  docsSuccessEnvelope: "Success envelope",
  docsListEnvelope: "List envelope",
  docsContentType: "Content type",
  docsApiKeyHeader: "API key header",
  docsTimestamps: "Timestamps",
  docsConventions: "API conventions",
  docsGroupAuth: "Authentication",
  docsGroupUsers: "Users",
  docsGroupKeys: "API keys",
  docsGroupKnowledge: "Knowledge",
  docsGroupRag: "RAG",
  docsGroupTasks: "Scheduled jobs",
  docsGroupRuns: "Execution runs",
  docsGroupLogs: "Logs",
  docsGroupCodex: "Codex",
  docsGroupDocs: "Documentation",
  docsGroupGeneral: "General",
  docsRouteAuthLogin: "Create a Django session.",
  docsRouteAuthLogout: "End the current session.",
  docsRouteAuthMe: "Read the current identity.",
  docsRouteUsersList: "List console users.",
  docsRouteUsersCreate: "Create a console user.",
  docsRouteUsersUpdate: "Update identity, role, activation, or password.",
  docsRouteUsersDelete: "Delete a console user.",
  docsRouteKeysList: "List API key metadata without returning secrets.",
  docsRouteKeysCreate: "Create a scoped key; plaintext is returned once.",
  docsRouteKeysDelete: "Revoke an API key.",
  docsRouteKnowledgeList: "Query an allow-listed knowledge table.",
  docsRouteKnowledgeCreate: "Create a knowledge record.",
  docsRouteKnowledgeRead: "Read one knowledge record.",
  docsRouteKnowledgeUpdate: "Partially update a knowledge record.",
  docsRouteKnowledgeDelete: "Delete a knowledge record.",
  docsRouteRagSearch:
    "Run vector search against the LanceDB compatibility index.",
  docsRouteTasksList: "List scheduled jobs with masked environment keys.",
  docsRouteTasksCreate: "Create a scheduled job.",
  docsRouteTasksUpdate: "Update a scheduled job.",
  docsRouteTasksDelete: "Delete configuration while retaining run history.",
  docsRouteTasksRun: "Queue a bounded job run.",
  docsRouteTaskRuns: "List a job's run history.",
  docsRouteRunRead: "Read bounded output and run status.",
  docsRouteLogsRead: "Read structured, redacted operations logs.",
  docsRouteCodexRuns:
    "List Codex runs with paging and start-time range filters.",
  docsRouteCodexRunEvents: "Read paginated events using the execution run ID.",
  docsRouteCodexExecute:
    "Execute a Codex task and return run_id for history and event queries.",
  docsRouteDocsRead: "Read the machine-readable API contract.",
} as const;

export type TranslationKey = keyof typeof en;
type TranslationDictionary = Record<TranslationKey, string>;

const zhCN: TranslationDictionary = {
  codexEventsTotal: "共 {count} 条可查日志",
  codexEventsLatest: "最新一页",
  codexEventsNew: "新增 {count} 条日志，已保留当前页。",
  codexEventsLegacy: "旧版快照：仅可查询当时保留的日志。",
  codexEventsMissing: "此前有 {count} 条日志未保留，无法恢复。",
  codexEventsTruncated: "此条日志文本在记录时已截断。",
  codexConfigSource: "Codex 配置来源",
  codexConfigCustom: "自定义连接",
  codexConfigLocal: "本机 Codex 配置",
  codexConfigLocalHelp:
    "使用 CPL 服务所在机器运行账户的 Codex 配置和登录状态；模型和推理档位留空时沿用该配置。",
  codexConfigInherit: "沿用本机配置",
  codexLocalScopeHelp:
    "允许此 Key 使用 CPL 服务运行账户的 Codex 配置与凭据，须单独授予该权限。",
  knowledgeUpdate: "更新知识库",
  knowledgeUpdateSelected: "更新知识库（{count}）",
  knowledgeUpdateScope: "所选仓库",
  knowledgeUpdateScopeHelp: "仅更新下列仓库。",
  knowledgeUpdateAdminOnly: "更新知识库需要管理员账号。",
  knowledgeUpdateEmpty: "请至少选择一个仓库。",
  knowledgeUpdateNotReady:
    "所选源码尚未就绪或未启用源码复制，请先在源码管理中准备。",
  knowledgeUpdateStart: "开始更新",
  knowledgeUpdateStop: "停止",
  knowledgeUpdateStopping: "停止中…",
  knowledgeUpdateStopped: "已停止",
  knowledgeUpdateStopFailed: "停止此更新失败：{message}",
  knowledgeUpdateQueued: "排队中",
  knowledgeUpdateAlreadyRunning: "所选仓库已有更新任务正在执行。",
  knowledgeUpdateViewExisting: "查看执行中的任务",
  knowledgeUpdateValidation: "导入摘要",
  knowledgeUpdateCoverageComplete: "知识覆盖完整。",
  knowledgeUpdateCoveragePartial: "本次报告未声明知识全覆盖，请查看导入摘要。",
  knowledgeUpdateValidationPending:
    "暂无导入摘要，请查看任务输出中的详细结果。",
  knowledgeUpdateVersions: "固定源码版本",
  knowledgeUpdateAgain: "配置下一次更新",
  knowledgeUpdateHistory: "打开 Codex 运行历史",
  knowledgeUpdateCatalogError: "模型目录暂不可用，可输入自定义模型或重试。",
  overviewInputTokens: "输入 Token（含缓存）",
  overviewInputCache: "输入缓存 / 缓存率",
  overviewOutputTokens: "输出token（含推理）",
  overviewCacheNote:
    "缓存率 = 缓存 Token / 对应方向的总 Token；SDK 未提供输出缓存，以 — 显示。",
  docsKeySingleSource:
    "请仅在专用 API key 输入框配置密钥，并移除自定义请求头中的 X-API-Key。",
  docsChooseFile: "请为此 multipart 请求选择上传文件。",
  docsPrefillHelp:
    "已填入接口方法、路径、请求头和参数示例，请替换变量及示例值。认证仅在 API key 输入框配置，发送时自动加入请求头，不会自动请求。",
  docsKeyPlaceholder: "填写 API key，切换接口时保留",
  docsBodyPlaceholder: "JSON 请求参数；GET 和 HEAD 无请求体",
  docsReplacePlaceholders: "请替换路径/查询占位变量，并配置 API key 后发送。",
  docsLocalPath: "请使用当前站点的 /api/ 路径。",
  docsUploadFile: "上传文件（其他 multipart 字段见上方参数）",
  usageDate: "日期",
  usageCacheNote: "缓存命中属于输入的一部分，Token 总量不再重复累加缓存。",
  usageInput: "输入 Token",
  usageOutput: "输出 Token",
  usageCached: "缓存命中 Token（包含在输入中）",
  usageCacheWrite: "缓存写入 Token（仅明细）",
  usageTotal: "Token 总量",
  usageReasoning: "推理输出token",
  usageUnavailable: "未提供",
  usageNote:
    "本次执行累计用量。缓存命中已包含在输入中，推理输出已包含在输出中；缓存明细不额外累加到总量。",
  usageWeek: "Codex 用量 · 最近 7 天",
  usageCoverage: "{runs} 次执行中 {samples} 次已统计，{missing} 次未提供",
  usageDuration: "耗时",
  usageAverage: "累计平均处理耗时",
  usageHistoryNote: "历史统计从现存记录回填，此前已清理的记录不包含在内。",
  usageTimezone: "每日统计按 {timezone} 划分，已排除模型可用性检测。",
  usageExecutions: "执行次数",

  overview: "总览",
  knowledge: "知识库",
  rag: "RAG 检索",
  jobs: "定时任务",
  history: "执行历史",
  logs: "系统日志",
  keys: "API 密钥",
  users: "用户与角色",
  codex: "Codex 调试",
  skills: "技能库",
  sources: "源码仓管理",
  sourceDescription: "在此选择定时更新的源码仓，执行计划由内置定时任务管理。",
  sourceAll: "更新全部仓库",
  sourceSelected: "更新所选仓库（{count}）",
  sourceOne: "更新",
  sourceLatest: "是否最新",
  sourceVerified: "最新",
  sourceStale: "非最新",
  sourceUnknown: "未确认",
  sourceLast: "上次更新时间",
  sourceNext: "下次自动更新时间",
  sourceScheduleLink: "管理更新计划",
  sourceResult: "上次更新结果",
  sourceHistory: "更新历史",
  sourceSuccess: "成功",
  sourceFailed: "失败",
  sourcePending: "等待更新",
  sourceRunning: "正在更新",
  sourceRetrying: "等待重试",
  sourceStart: "开始时间",
  sourceEnd: "结束时间",
  sourceDuration: "用时",
  sourceCommit: "更新后 commit ID",
  sourceError: "失败原因 / 重试详情",
  sourceBranch: "分支",
  sourceBatch: "更新批次详情",
  sourceOmissions: "Windows 无法落盘的路径（Git 原始内容保留）",
  sourceManual: "手动更新",
  sourceHourly: "定时更新 / 初始化",
  sourceAutoOff: "源码仓自动更新已停用",
  sourceAccepted: "已提交更新请求，本页每 3 秒刷新状态。",
  sourceEmpty: "暂无更新历史。",
  sourceTrigger: "触发方式",
  sourceTotal: "仓库总数",
  sourceInterrupted: "已中断",
  sourceExecuting: "执行中",
  sourceStop: "停止",
  sourceStopping: "停止中",
  sourceStopped: "已停止",
  sourceBehind: "落后 {count} 次提交",
  sourceDistanceUnknown: "提交数量未确认",
  sourceProgress: "更新进度",
  sourceProgressWaiting: "等待 Git 进度输出…",
  sourceSkipped: "跳过（正在执行）",
  sourceRemote: "远端分支 HEAD",
  sourceRemoteChecked: "远端 HEAD 查询时间",
  sourceAuto: "保持最新",
  sourceCopy: "复制到检查目录",
  sourceAdd: "新增源码仓",
  sourceEdit: "编辑源码仓",
  sourceUrl: "Git HTTPS 地址",
  sourceSaved: "源码仓配置已保存。",
  sourceDelete: "删除此仓库及集中缓存？已经开始的任务副本会保留。",
  docs: "API 文档",
  signOut: "退出登录",
  signIn: "登录",
  profile: "个人资料",
  changePassword: "修改密码",
  currentPassword: "当前密码",
  newPassword: "新密码",
  registerAction: "注册",
  setupTitle: "初始化管理员",
  setupCode: "本地文件验证码",
  setupHelp:
    "读取运行服务的机器上项目目录中的以下文件，输入验证码。首个管理员创建后立即生效。",
  setupSuccess: "管理员已创建，请使用新账户登录。",
  registerTitle: "创建账号",
  registerEmail: "邮箱",
  registerConfirmPassword: "确认密码",
  registerPending: "注册申请已提交，请等待管理员审批。",
  operational: "所有系统运行正常",
  menu: "菜单",
  start: "开始",
  language: "English",
  loading: "加载中...",
  create: "新建",
  remove: "删除",
  edit: "编辑",
  run: "运行",
  name: "名称",
  state: "状态",
  action: "操作",
  actions: "操作",
  empty: "暂无记录。",
  details: "详情",
  preview: "预览",
  close: "关闭",
  raw: "原始数据",
  save: "保存",
  cancel: "取消",
  working: "处理中...",
  error: "错误",
  success: "成功",
  enabled: "已启用",
  disabled: "已禁用",
  selectAll: "全选",
  deselectAll: "取消全选",
  active: "正常",
  enable: "启用",
  disable: "禁用",
  previous: "上一页",
  next: "下一页",
  goToPage: "跳转",
  paginationLabel: "分页",
  paginationPageSummary: "第 {page} 页，共 {total} 页",
  paginationPageInput: "页码",
  paginationInvalidPage: "请输入 1 到 {total} 之间的整数。",
  refresh: "刷新",
  sortAscending: "按{column}升序排列",
  sortDescending: "按{column}降序排列",
  sortUnsorted: "取消按{column}排序",
  codexMissingFailureReason:
    "服务端未记录具体失败原因，请结合执行详情和请求编号排查。",
  codexCustomModel: "自定义模型",
  codexExecutionMode: "执行模式",
  codexTimeoutSeconds: "执行时限（秒，1–{seconds}，最长{hours}小时）",
  codexRestricted: "受限执行",
  codexUnrestricted: "不受限执行（管理员或特权 API Key）",
  tableColumnResizeHelp: "拖动调整列宽；左/右键调整10像素，Shift加速至50像素",
  tableColumnMoveHelp: "拖动调整列顺序，或聚焦后按 Alt + 左/右方向键",
  selectVisible: "全选筛选结果",
  deselectVisible: "取消筛选结果选择",
  shellSubtitle: "兼容性运营",
  shellControlPlane: "控制台",
  breadcrumbConsole: "控制台",
  backToOverview: "返回总览",
  loginHeading: "仓颉兼容性平台 / 安全登录",
  loginTitle: "兼容性平台",
  loginDescription: "知识库运营控制台",
  loginMarquee: "授权访问 / 本地控制台",
  loginUsername: "邮箱 / 用户名",
  wallpaperTitle: "显示属性",
  wallpaperDesktop: "桌面",
  wallpaperStars: "星空",
  wallpaperPipes: "管道",
  wallpaperMaze: "迷宫",
  wallpaperPause: "暂停动画",
  wallpaperPlay: "播放动画",
  loginPassword: "密码",
  loginSubmit: "进入控制台",
  loginDefaultStatus: "请使用管理员凭据登录。",
  loginDocs: "查看公开 API 文档",
  overviewKicker: "总览 / 运行状态",
  overviewTitle: "控制台总览",
  overviewDescription: "查看服务状态、资源清单和最近的自动化任务。",
  overviewMarquee: "公开读取 API 在线 / 写操作需要 X-API-KEY",
  overviewKnowledgeTables: "知识资源",
  overviewRepositories: "仓库",
  overviewComponents: "组件",
  overviewFiles: "文件",
  overviewSymbols: "符号",
  overviewContracts: "契约",
  overviewEvidence: "证据",
  overviewBehaviors: "行为",
  overviewConsumers: "消费者",
  overviewScheduledJobs: "定时任务",
  overviewActiveJobs: "启用中的任务",
  overviewSkills: "技能",
  overviewApiKeys: "API 密钥",
  overviewRagBackend: "RAG 后端",
  overviewOnline: "在线",
  overviewChecking: "检查中...",
  overviewInventory: "运行资源清单",
  overviewRecentJobs: "最近的定时任务",
  overviewRecentRuns: "最近的 Codex 执行",
  overviewEmptyRuns: "尚未记录 Codex 执行。",
  overviewRunModel: "模型",
  overviewRunStatus: "状态",
  overviewRunTime: "开始时间",
  overviewLoading: "正在加载运行资源清单...",
  overviewEmptyJobs: "尚未配置定时任务。",
  overviewJob: "任务",
  overviewCommand: "命令",
  overviewState: "状态",
  overviewLoadError: "无法加载运行资源清单：{message}",
  formInvalidJson: "请输入有效的 JSON。",
  formObjectRequired: "请输入 JSON 对象。",
  formFieldsRequired: "请填写所有必填字段。",

  knowledgeField_has_markdown: "Markdown 内容",
  knowledgeField_markdown_document_id: "关联 Markdown 文档",
  knowledgeChooseRepositoryFirst: "请先选择仓库",
  knowledgeYes: "有",
  knowledgeNo: "无",
  knowledgeChooseReference: "搜索并选择一条记录",
  knowledgeNewLocation: "保存时创建或复用来源位置",
  knowledgeExistingLocation: "选择已有位置",
  knowledgeHasMarkdown: "已转换的 Markdown 内容",
  knowledgeTitle: "知识资源",
  knowledgeDescription: "搜索、查看并管理兼容性知识记录。",
  knowledgeArchitectureTitle: "知识库架构",
  knowledgeArchitectureDescription:
    "沿来源位置查看关联的符号、契约、证据与审核决策。",
  knowledgeArchitectureMapLabel: "知识库架构图",
  knowledgeArchitectureSources: "来源登记",
  knowledgeArchitectureRepositories: "仓库",
  knowledgeArchitectureRepositoriesHint: "项目与平台身份",
  knowledgeArchitectureFiles: "文件",
  knowledgeArchitectureFilesHint: "Markdown、源码和导入文档",
  knowledgeArchitectureIndex: "结构索引",
  knowledgeArchitectureLocations: "位置",
  knowledgeArchitectureLocationsHint: "行、页和章节坐标",
  knowledgeArchitectureChunks: "分块",
  knowledgeArchitectureChunksHint: "可搜索的文档与源码片段",
  knowledgeArchitectureSymbols: "符号",
  knowledgeArchitectureSymbolsHint: "命名声明与签名",
  knowledgeArchitectureSemantic: "语义视图",
  knowledgeArchitectureContracts: "契约",
  knowledgeArchitectureContractsHint: "兼容承诺与风险",
  knowledgeArchitectureEvidence: "证据",
  knowledgeArchitectureEvidenceHint: "带来源的观测事实",
  knowledgeArchitectureCases: "案例",
  knowledgeArchitectureCasesHint: "审核决策与关联记录",
  knowledgeArchitectureRelationNote:
    "箭头表示记录间的关联，不代表自动生成流程。点击记录可打开对应查询视图。",
  knowledgeArchitectureLocationLink: "来源定位",
  knowledgeArchitectureCaseLink: "审核引用",
  knowledgeArchitectureSourceLink: "命名空间",
  knowledgeArchitectureViewsTitle: "打开视图",
  knowledgeArchitectureViewsDescription:
    "每个视图都是同一知识图谱上的维护投影。选择一个视图查看记录。",
  knowledgeCreate: "新建记录",
  knowledgeUploadFile: "上传文件",
  upload: "上传",
  file: "文件",
  namespace: "命名空间",
  relative_path: "相对路径",
  knowledgeSearchPlaceholder: "输入搜索...",
  knowledgeTable: "数据表",
  knowledgeSearch: "搜索",
  knowledgePageSize: "每页数量",
  knowledgeRecords: "{table} 记录",
  knowledgePageSummary: "第 {page} 页 / {count} 条结果",
  knowledgeRecordDetail: "记录详情",
  knowledgeEmpty: "当前筛选条件下没有知识记录。",
  knowledgeInvalidPage: "请输入有效的页码范围。",
  knowledgeCreateTitle: "新建知识记录",
  knowledgeEditTitle: "编辑知识记录",
  knowledgeDeleteTitle: "删除知识记录",
  knowledgeConfirmDelete: "确定删除记录 {id}？",
  knowledgeCreated: "知识记录已创建。",
  knowledgeUpdated: "知识记录已更新。",
  knowledgeDeleted: "知识记录已删除。",
  knowledgeTabSearch: "搜索",
  knowledgeTabContracts: "契约",
  knowledgeTabEvidence: "证据",
  knowledgeTabSymbols: "符号",
  knowledgeTabFiles: "文件",
  knowledgeTabRepositories: "仓库",
  knowledgeTabCases: "分析案例",
  knowledgeTab_search: "知识库架构",
  knowledgeTab_contracts: "契约",
  knowledgeTab_evidence: "证据",
  knowledgeTab_symbols: "符号",
  knowledgeTab_chunks: "内容分块",
  knowledgeTab_files: "文件",
  knowledgeTab_documents: "Markdown 文档",
  knowledgeDownloadContent: "下载完整文档",
  knowledgePreviewLimited: "预览显示前 200,000 个字符，下载后可查看完整文档。",
  knowledgeUploadLimits:
    "支持 .md、.txt、.pdf、.docx 文件，文件大小不超过 20 MiB。",
  knowledgeTab_locations: "来源位置",
  knowledgeTab_components: "组件",
  knowledgeTab_repositories: "仓库",
  knowledgeTab_cases: "分析案例",
  "knowledgeTab_ingestion-sources": "导入来源",
  knowledgeSelect: "请选择",
  knowledgeOnePerLine: "每行填写一个值。",
  knowledgeContractDetail: "契约类型详情",
  knowledgeEvidenceLinks: "证据关联",
  knowledgeAddEvidence: "添加证据",
  knowledgeAddBehavior: "添加行为",
  knowledgeAddConsumer: "添加消费者",
  knowledgeObservations: "观察记录",
  knowledgeAddObservation: "添加观察记录",
  knowledgeRelationCreated: "关联记录已创建。",
  knowledgeRelationDeleted: "关联记录已删除。",
  knowledgeFileMetadata: "文件信息",
  knowledgeFileContent: "文件内容",
  knowledgeBehaviors: "行为",
  knowledgeConsumers: "消费者",
  knowledgeHardDeleteHint: "此操作会永久删除资源；仍被引用的资源无法删除。",
  knowledgeDeleteFailed: "删除失败，资源可能仍被引用",
  knowledgeDeleteAction: "删除",
  knowledgeSuggestionsLoading: "正在加载建议...",
  knowledgeSuggestionsError: "暂时无法加载搜索建议。",
  knowledgeResultCount: "共 {count} 条结果",
  knowledgeQueryRequired: "请输入关键词后搜索知识库。",
  knowledgeTypes: "资源类型",
  knowledgeType: "类型",
  knowledgeMatch: "匹配模式",
  knowledgeMinimumMatch: "最少匹配词数",
  knowledgeRepository: "仓库",
  knowledgeComponent: "组件",
  knowledgeKind: "类别",
  knowledgeFamily: "契约族",
  knowledgeRisk: "风险面",
  knowledgeLevel: "证据级别",
  knowledgeIngestionSource: "导入来源",
  knowledgePath: "来源路径",
  knowledgeCitation: "引用位置",
  knowledgeTitleColumn: "标题",
  knowledgeSummary: "摘要",
  knowledgeAll: "全部",

  keysKicker: "访问控制 / 凭据",
  keysTitle: "API 密钥",
  keysDescription: "具有作用域的访问凭据；明文仅在创建时显示一次。",
  keysGenerate: "生成密钥",
  keysCopySecret: "请立即复制密钥",
  keysInventory: "凭据清单",
  keysName: "凭据名称",
  keysPrefix: "前缀",
  keysScopes: "权限范围",
  keysCreated: "创建时间",
  keysLastUsed: "最后使用",
  keysCreateTitle: "生成 API 密钥",
  keysDeleteRecord: "删除记录",
  keysDeletedStatus: "已吊销密钥的记录已删除。",
  keysConfirmDelete: "永久删除这条已吊销密钥记录？历史审计记录将保留。",
  keysRevoke: "吊销",
  keysConfirmRevoke: "确定吊销此 API 密钥？",
  keysEmpty: "尚未创建 API 密钥。",
  keysCreatedStatus: "API 密钥已创建，请立即复制密钥。",
  keysRevokedStatus: "API 密钥已吊销。",

  usersKicker: "访问控制 / 身份",
  settings: "系统设置",
  settingsDescription: "管理执行与排队容量。",
  settingsAdminOnly: "仅管理员可以访问系统设置。",
  capacityTitle: "请求容量",
  capacityExecution: "长请求最大执行数（10–100）",
  capacityQueue: "最大排队数（0–100）",
  capacityActive: "当前执行中",
  capacityWaiting: "当前排队中",
  capacityHelp:
    "适用于 Codex 执行、模型查询等 HTTP 长请求；异步知识更新使用独立队列。",
  capacityBehavior:
    "有执行名额时直接运行，执行已满则排队，队列也满时新请求返回 HTTP 503。保存后立即生效；降低上限不会终止已执行或已排队请求。刷新会重新读取已保存配置及当前计数。",
  capacityInvalid: "执行数须为 10–100 的整数，排队数须为 0–100 的整数。",
  capacitySaved: "已保存并立即生效。",
  usersTitle: "用户与角色",
  usersDescription: "管理控制台用户、角色和启用状态。",
  usersAdd: "添加用户",
  usersDirectory: "用户目录",
  usersUser: "用户",
  usersRole: "角色",
  usersLastLogin: "最后登录",
  usersPassword: "密码",
  usersNewPassword: "新密码",
  usersCreateTitle: "添加控制台用户",
  usersEditTitle: "编辑控制台用户",
  usersDelete: "删除用户",
  usersConfirmDelete: "确定删除 {username}？",
  usersEmpty: "未找到控制台用户。",
  usersCreatedStatus: "控制台用户已创建。",
  usersUpdatedStatus: "控制台用户已更新。",
  usersApprovedStatus: "控制台用户已批准。",
  usersApprove: "批准",
  usersDeletedStatus: "控制台用户已删除。",
  usersPasswordOptional: "留空则保持当前密码",

  jobsKicker: "自动化 / 调度器",
  jobsTitle: "定时任务",
  jobsDescription: "管理 Python 命令、调度周期、环境变量和执行状态。",
  jobsCreate: "创建任务",
  jobsInventory: "任务清单",
  jobsCommand: "命令",
  jobsShell: "Shell",
  jobsCategory: "任务类别",
  jobsCategoryScript: "脚本",
  jobsCategoryCodex: "Codex",
  jobsProviderBaseUrl: "服务商接口地址",
  jobsProviderApiKey: "服务商 API 密钥",
  jobsProviderApiKeyKeep: "留空则保持当前密钥",
  jobsModel: "模型",
  jobsEffort: "推理强度",
  jobsSkills: "技能",
  jobsSkillsPlaceholder: "用逗号分隔技能名称",
  jobsSystemPrompt: "系统提示词",
  jobsPrompt: "提示词",
  jobsScript: "内联脚本",
  jobsWorkingDirectory: "工作目录",
  jobsInterval: "执行间隔（秒）",
  jobsTimeout: "超时时间（秒，1–{seconds}，最长{hours}小时）",
  jobsEnvironment: "环境变量",
  jobsEnvironmentKey: "键",
  jobsEnvironmentValue: "值",
  jobsEnvironmentKeep: "已保存，留空保留原值",
  jobsEnvironmentClear: "清空全部已保存变量",
  jobsEnvironmentKeyInvalid: "请输入有效的环境变量名。",
  jobsEnvironmentDuplicate: "环境变量名不能重复。",
  jobsShowEnvironmentValue: "显示值",
  jobsHideEnvironmentValue: "隐藏值",
  jobsAddEnvironment: "添加变量",
  jobsDescriptionMarkdown: "介绍（Markdown）",
  jobsTaskType: "任务类型",
  jobsTaskTypeBash: "Bash脚本",
  jobsTaskTypePowershell: "PowerShell脚本",
  jobsTaskTypeCmd: "CMD脚本",
  jobsBuiltinSource: "内置源码仓更新",
  jobsBuiltinSourceHelp:
    "固定系统脚本读取源码仓管理维护的仓库目录清单，请在源码仓管理中调整更新名单。",
  jobsStartAt: "计划生效时间（本地时间）",
  jobsStartAtHelp:
    "可选的生效起点；留空保留已保存的计划锚点，Cron 在其后匹配时刻执行。",
  jobsTaskTypePython: "Python脚本",
  jobsTaskTypeJavascript: "JavaScript脚本",
  jobsTaskTypeCodex: "Codex任务",
  jobsScheduleMode: "定时模式",
  jobsScheduleModeFixed: "每天固定时间执行",
  jobsScheduleModeInterval: "固定间隔",
  jobsScheduleModeCron: "Cron 表达式",
  jobsCronExpression: "Cron 表达式",
  jobsCronHelp:
    "五字段：分钟 小时 日 月 星期；工作日可用 mon-fri（0 为周一）。",
  jobsTimezone: "时区（IANA）",
  historyAllTasks: "全部任务",
  historyExportExcel: "导出 Excel",
  historyExportFrom: "开始日期",
  historyExportTo: "结束日期（含当天）",
  historyExportHelp: "导出符合当前筛选与日期范围的全部已保存记录，不限当前页。",
  historyExportInvalidDate: "请选择有效日期，结束日期不能早于开始日期。",
  historyExportBusy: "正在导出…",
  jobsFixedHour: "时（0-23）",
  jobsFixedMinute: "分（0-59）",
  jobsStartHour: "开始时（0-23）",
  jobsStartMinute: "开始分（0-59）",
  jobsEndHour: "结束时（0-23）",
  jobsEndMinute: "结束分（0-59）",
  jobsIntervalSeconds: "间隔秒数",
  jobsCreateTitle: "创建定时任务",
  jobsEditTitle: "编辑定时任务",
  jobsConfirmDelete: "确定删除此任务配置？",
  jobsEmpty: "尚未配置定时任务。",
  jobsCreatedStatus: "定时任务已创建。",
  jobsUpdatedStatus: "定时任务已更新。",
  jobsDeletedStatus: "定时任务已删除。",
  jobsQueuedStatus: "任务执行已加入队列。",
  jobsEnabled: "启用",

  historyKicker: "自动化 / 审计",
  historyTitle: "执行历史",
  historyDescription: "选择任务并查看每次执行的输出与状态。",
  historyTask: "任务",
  historyNoTasks: "暂无任务",
  historyRuns: "执行记录",
  historyRun: "执行",
  historyStatus: "状态",
  historyStarted: "开始时间",
  historyFinished: "结束时间",
  historyReturnCode: "返回码",
  historyStdout: "标准输出",
  historyStderr: "标准错误",
  historyRunDetail: "执行详情",
  historyEmpty: "此任务暂无执行记录。",

  logsKicker: "可观测性 / 系统",
  logsTitle: "API 审计日志",
  logsDescription: "结构化 API 请求记录，与系统运行日志独立保存。",
  logsLatest: "最新事件",
  logsTimestamp: "时间",
  logsLevel: "级别",
  logsEvent: "事件",
  logsMessage: "消息",
  logsCorrelation: "关联标识",
  logsFilters: "筛选条件",
  logsAll: "全部级别",
  logsEmpty: "当前筛选条件下没有日志事件。",
  logsTask: "任务",
  logsFrom: "开始时间",
  logsTo: "结束时间",
  logsMethod: "方法",
  logsStatus: "状态码",
  logsActor: "操作者",
  logsResource: "资源",
  logsDuration: "耗时",
  logsDetail: "审计详情",
  logsDetailLoading: "正在加载审计详情...",
  logsDetailLoadError: "无法加载审计详情：{message}",
  logsRequestId: "请求 ID",
  logsPath: "路径",
  logsFields: "请求字段",
  logsRedaction: "脱敏信息",

  ragKicker: "检索 / LANCEDB",
  ragTitle: "RAG 检索",
  ragDescription: "查看排序后的兼容性上下文及实际检索后端。",
  ragRequest: "检索请求",
  ragQuestion: "兼容性问题",
  ragQuestionPlaceholder: "输入兼容性相关问题",
  ragTopK: "结果数量",
  ragSearch: "检索语料库",
  ragResults: "排序结果",
  ragInitial: "请输入一个兼容性问题。",
  ragLanceStatus: "LanceDB 向量检索",
  ragError: "检索失败：{message}",
  ragEmpty: "未返回相关上下文。",
  ragResultFallback: "结果 {index}",

  codexKicker: "AI / 模型网关",
  codexTitle: "Codex 调试",
  codexDescription: "执行兼容 OpenAI 的请求并查看标准化输出。",
  codexConfiguration: "请求配置",
  codexProviderKey: "服务商 API 密钥",
  codexProtocol: "上游协议",
  codexRequestedEffort: "请求推理强度",
  codexEffectiveEffort: "实际推理强度",
  codexEffortOmitted: "未传入（使用模型默认）",
  codexEffortAdjustments: "推理设置调整",
  codexBaseUrl: "接口地址",
  codexModel: "模型",
  codexEffort: "推理强度",
  codexSkills: "技能",
  codexSkillsHelp: "选择要在本次隔离运行中启用的技能。",
  skillMultiSelectTitle: "技能",
  skillMultiSelectFilter: "筛选技能",
  skillMultiSelectEmpty: "没有匹配的技能。",
  codexSystemPrompt: "系统提示词",
  codexPrompt: "用户提示词",
  codexEnvironment: "环境变量",
  codexEnvironmentKey: "键",
  codexEnvironmentValue: "值",
  codexEnvironmentKeyInvalid: "请输入有效的环境变量名。",
  codexEnvironmentDuplicate: "环境变量名不能重复。",
  codexShowEnvironmentValue: "显示值",
  codexHideEnvironmentValue: "隐藏值",
  codexAddEnvironment: "添加变量",
  codexExecute: "执行请求",
  codexResult: "执行结果",
  codexResultModel: "模型",
  codexResultEffort: "推理强度",
  codexResultUsage: "Token 用量",
  codexResultLatency: "延迟",
  codexResultRequestId: "请求 ID",
  codexInitial: "尚未执行请求。",
  codexInvalidEnvironment: "环境变量必须是 JSON 对象。",
  codexError: "执行失败：{message}",
  codexHistoryTitle: "运行历史",
  codexHistoryStatus: "状态",
  codexHistoryAll: "全部",
  codexHistoryAvailabilityProbe: "是否为模型可用性检测",
  codexHistoryProbeYes: "是",
  codexHistoryProbeNo: "否",
  codexHistoryRunning: "运行中",
  codexProgressTitle: "执行进度",
  codexProgressRefresh: "每 3 秒刷新；完成后保留步骤记录。",
  codexProgressOmitted:
    "已省略较早的 {count} 条记录；保留最近 200 条，单条最多 4000 字符。",
  codexProgressEmpty: "暂无步骤记录（旧记录没有执行时间线）。",
  codexHistorySucceeded: "成功",
  codexHistoryFailed: "失败",
  codexHistoryFrom: "启动时间从",
  codexHistoryTo: "启动时间至",
  codexHistoryTimeHelp:
    "按本地时区筛选任务记录创建/接收时间，包含起止边界；排队任务的模型实际执行可能晚于此时间。",
  codexHistoryPageSize: "每页条数",
  codexHistoryFilter: "应用筛选",
  codexHistoryCreated: "发起时间",
  codexHistoryEmpty: "没有符合筛选条件的 Codex 运行记录。",
  codexHistoryLoadError: "无法加载运行历史：{message}",
  codexHistoryDetail: "Codex 运行详情",
  codexHistoryDetailError: "无法加载运行详情：{message}",
  codexHistoryOutput: "AI 输出",
  codexHistoryRequest: "请求内容",
  codexHistorySystemPrompt: "系统提示词",
  codexHistorySkills: "技能",
  codexHistoryEnvKeys: "环境变量名称",
  codexHistoryWorkspace: "调试工作区",
  codexHistoryKeyHashLength: "API 密钥哈希长度",
  codexHistoryError: "错误",
  skillsKicker: "AI / 技能仓库",
  skillsTitle: "技能仓库",
  skillsDescription: "管理可供隔离 Codex 运行使用的已验证技能。",
  skillsInventory: "已安装技能",
  skillImport: "导入或替换技能",
  skillDescription: "描述",
  skillValidation: "验证状态",
  skillUpdated: "更新时间",
  skillEmpty: "尚未安装技能。",
  skillEnabled: "技能已启用。",
  skillDisabled: "技能已停用。",
  skillImported: "技能已导入。",
  skillDeleted: "技能已删除。",
  skillConfirmDelete: "确定删除技能 {name}？",
  skillPreviewTitle: "技能详情：{name}",
  skillPreviewLoading: "正在加载技能详情...",
  skillFileCount: "文件数",
  skillSize: "大小（字节）",
  skillFiles: "技能文件",
  skillFilesEmpty: "该技能没有可显示的文件。",
  skillFileSelect: "选择文本文件进行预览或编辑。",
  skillFileText: "文本",
  skillFileReadOnly: "只读的二进制或超大文件",
  skillFileLoading: "正在加载文件...",
  skillFileContent: "{path} 的内容",
  skillFileUnsaved: "有未保存的更改",
  skillFileSaved: "已保存 {path}。",

  docsKicker: "公开开发者资源",
  docsTitle: "仓颉兼容性 API 调试器",
  docsDescription: "知识库、RAG 与 Codex 外部 API 的实时接口合同。",
  docsConsole: "管理员控制台",
  docsDebugger: "在线请求调试",
  docsMethod: "请求方法",
  docsPath: "请求路径",
  docsHeaders: "附加请求头（JSON）",
  docsApiKey: "API 密钥",
  docsBody: "JSON 请求体",
  docsSend: "发送请求",
  docsTryIt: "调试此接口",
  docsLoading: "正在加载 API 合同...",
  docsResponse: "响应",
  docsResponsePending: "响应结果将显示在此处。",
  docsLoadError: "无法加载 API 合同：{message}",
  docsInvalidHeaders: "附加请求头必须是 JSON 对象。",
  docsInvalidBody: "请求体必须是有效的 JSON。",
  docsNetworkError: "网络请求失败：{message}",
  docsHttpResult: "HTTP {status} {statusText}",
  docsAuthentication: "认证方式",
  docsAuthDescription: "受保护的操作通过 X-API-Key 请求头接收 API 密钥。",
  docsAuthRequired: "需要 X-API-Key（{scope}）",
  docsAuthNotRequired: "无需 X-API-Key",
  docsEndpointPurpose: "用途",
  docsEndpoints: "接口列表",
  docsPublic: "公开访问",
  docsSession: "登录会话",
  docsApiKeyAuth: "API 密钥",
  docsQuery: "查询参数",
  docsNoQuery: "此接口没有查询参数。",
  docsRequestSchema: "请求体",
  docsResponseExample: "响应示例",
  docsResponseSchema: "响应合同",
  docsErrors: "错误码",
  docsTables: "允许的数据表",
  docsExamples: "调用示例",
  docsCurl: "curl",
  docsPython: "Python",
  docsJavaScript: "JavaScript",
  docsNoRequestBody: "此接口没有请求体。",
  docsNoErrors: "可能返回标准 HTTP 和权限错误。",
  docsSuccessEnvelope: "成功响应包络",
  docsListEnvelope: "列表响应包络",
  docsContentType: "内容类型",
  docsApiKeyHeader: "API 密钥请求头",
  docsTimestamps: "时间格式",
  docsConventions: "API 约定",
  docsGroupAuth: "身份认证",
  docsGroupUsers: "用户",
  docsGroupKeys: "API 密钥",
  docsGroupKnowledge: "知识库",
  docsGroupRag: "RAG",
  docsGroupTasks: "定时任务",
  docsGroupRuns: "执行记录",
  docsGroupLogs: "日志",
  docsGroupCodex: "Codex",
  docsGroupDocs: "接口文档",
  docsGroupGeneral: "其他",
  docsRouteAuthLogin: "创建 Django 登录会话。",
  docsRouteAuthLogout: "结束当前登录会话。",
  docsRouteAuthMe: "读取当前用户身份。",
  docsRouteUsersList: "列出控制台用户。",
  docsRouteUsersCreate: "创建控制台用户。",
  docsRouteUsersUpdate: "更新用户身份、角色、状态或密码。",
  docsRouteUsersDelete: "删除控制台用户。",
  docsRouteKeysList: "列出 API 密钥元数据，不返回密钥明文。",
  docsRouteKeysCreate: "创建有权限范围的密钥；明文仅返回一次。",
  docsRouteKeysDelete: "吊销 API 密钥。",
  docsRouteKnowledgeList: "查询允许访问的知识数据表。",
  docsRouteKnowledgeCreate: "创建知识记录。",
  docsRouteKnowledgeRead: "读取单条知识记录。",
  docsRouteKnowledgeUpdate: "部分更新知识记录。",
  docsRouteKnowledgeDelete: "删除知识记录。",
  docsRouteRagSearch: "在 LanceDB 兼容性索引中执行向量检索。",
  docsRouteTasksList: "列出定时任务，环境变量值将被隐藏。",
  docsRouteTasksCreate: "创建定时任务。",
  docsRouteTasksUpdate: "更新定时任务。",
  docsRouteTasksDelete: "删除任务配置并保留执行历史。",
  docsRouteTasksRun: "将受限的任务执行加入队列。",
  docsRouteTaskRuns: "列出任务的执行历史。",
  docsRouteRunRead: "读取受限长度的输出和执行状态。",
  docsRouteLogsRead: "读取结构化且已脱敏的运维日志。",
  docsRouteCodexRuns: "按启动时间范围分页查询 Codex 执行记录。",
  docsRouteCodexRunEvents: "使用执行返回的运行 ID 分页查询事件。",
  docsRouteCodexExecute: "执行 Codex 任务并返回 run_id，用于记录与事件查询。",
  docsRouteDocsRead: "读取机器可读的 API 合同。",
};

const messages: Record<Language, TranslationDictionary> = {
  en,
  "zh-CN": zhCN,
};

function storedLanguage(): Language {
  const stored = localStorage.getItem("language");
  return stored === "en" || stored === "zh-CN" ? stored : "zh-CN";
}

let language = storedLanguage();

export const i18n = {
  get language(): Language {
    return language;
  },

  t(
    key: TranslationKey,
    variables: Record<string, string | number> = {},
  ): string {
    return Object.entries(variables).reduce(
      (message, [name, value]) =>
        message.replaceAll(`{${name}}`, String(value)),
      messages[language][key],
    );
  },

  set(nextLanguage: Language): void {
    if (language === nextLanguage) return;
    language = nextLanguage;
    localStorage.setItem("language", language);
    document.documentElement.lang = language;
    window.dispatchEvent(new Event("languagechange"));
  },

  toggle(): void {
    this.set(language === "en" ? "zh-CN" : "en");
  },
};

document.documentElement.lang = language;
